import React, { useState } from 'react';
import { ExternalLink, BookOpen, ShieldAlert, ChevronDown, ChevronUp, PenLine, Library } from 'lucide-react';
import { Section, Card, IconTile } from '../../hospital/shared/primitives';

const ACCENT = '#047857';

const GUIDELINES = [
  ['CONSORT', 'Randomized controlled trials', 'http://www.consort-statement.org/'],
  ['STROBE', 'Cohort, case-control and cross-sectional studies', 'https://www.strobe-statement.org/'],
  ['PRISMA', 'Systematic reviews and meta-analyses', 'http://www.prisma-statement.org/'],
  ['CARE', 'Case reports', 'https://www.care-statement.org/'],
];

const BY_TYPE = [
  ['Case report', 'A single patient, an unusual presentation or a new technique', 'Anesthesia & Analgesia, JCVA, Pediatric Anesthesia, IJOA'],
  ['Randomized controlled trial', 'Prospective, randomized, with a control group', 'Anesthesiology, BJA, Anesthesia & Analgesia, RAPM'],
  ['Retrospective or observational study', 'Chart review, database analysis, cohort study', 'Any of the journals below; clinical significance and sound statistics decide'],
  ['Quality improvement', 'Process change, safety initiative, implementation', 'Anesthesia & Analgesia, JCVA, Pediatric Anesthesia, IJOA'],
  ['Systematic review or meta-analysis', 'Literature synthesis with statistical pooling', 'Anesthesiology, BJA, Anesthesia & Analgesia'],
  ['Technical report', 'A new technique, equipment modification or procedural tip', 'RAPM, JCVA, Pediatric Anesthesia'],
];

const JOURNALS = [
  { name: 'Anesthesiology', publisher: 'ASA', impact: '8.99', accept: '~15%', time: '3 to 6 months', note: 'Novel trials, meta-analyses and practice-changing work. Rigorous statistics expected.', url: 'https://pubs.asahq.org/anesthesiology/pages/instructions-for-authors' },
  { name: 'Anesthesia & Analgesia', publisher: 'IARS', impact: '5.7', accept: '~25%', time: '2 to 4 months', note: 'Broader scope than Anesthesiology; accepts case reports and smaller studies. A good first target for trainees.', url: 'https://www.anesthesia-analgesia.org/pages/authorinfo' },
  { name: 'British Journal of Anaesthesia', publisher: 'BJA', impact: '9.8', accept: '~18%', time: '2 to 5 months', note: 'International multicentre trials and systematic reviews. Selective.', url: 'https://academic.oup.com/bja/pages/General_Instructions' },
  { name: 'Regional Anesthesia and Pain Medicine', publisher: 'ASRA', impact: '4.9', accept: '~30%', time: '2 to 3 months', note: 'Nerve blocks, ultrasound techniques and pain research. Welcomes technical innovation.', url: 'https://rapm.bmj.com/pages/authors/' },
  { name: 'Journal of Cardiothoracic and Vascular Anesthesia', publisher: 'SCA', impact: '2.9', accept: '~35%', time: '2 to 4 months', note: 'Cardiac studies, TEE research, ERAS protocols and cardiac case reports.', url: 'https://www.jcvaonline.com/content/authorinfo' },
  { name: 'Pediatric Anesthesia', publisher: 'Wiley', impact: '2.5', accept: '~30%', time: '2 to 4 months', note: 'Age-specific studies, pediatric techniques and safety. Receptive to trainee submissions.', url: 'https://onlinelibrary.wiley.com/page/journal/11555645/homepage/forauthors.html' },
  { name: 'International Journal of Obstetric Anesthesia', publisher: 'Elsevier', impact: '2.8', accept: '~35%', time: '2 to 4 months', note: 'Labor analgesia, cesarean delivery and obstetric complications. Accepts case reports and QI.', url: 'https://www.elsevier.com/journals/international-journal-of-obstetric-anesthesia/0959-289X/guide-for-authors' },
];

const RED_FLAGS = [
  'Unsolicited email promising rapid publication',
  'Fees beyond standard open-access charges',
  'No clear description of peer review',
  'Not indexed in PubMed or MEDLINE',
  'A poorly made website with grammatical errors',
  'An invented or inflated impact factor',
  'An editorial board outside the field',
];

const CHECKS = [
  ['Think. Check. Submit.', 'A short checklist for identifying trusted journals', 'https://thinkchecksubmit.org/'],
  ['Directory of Open Access Journals', 'The whitelist of legitimate open-access journals', 'https://doaj.org/'],
  ['NLM Catalog', 'Confirm a journal is indexed', 'https://www.ncbi.nlm.nih.gov/nlmcatalog/journals'],
];

const WRITING = [
  ['Start with the figures and tables', 'They carry the story; the manuscript is built around them.'],
  ['Write the methods first', 'The easiest section, and it clarifies the approach.'],
  ['Make the abstract stand alone', 'For many readers it is the whole paper.'],
  ['Follow the journal guidelines exactly', 'Format, word count and reference style.'],
  ['Seek feedback early', 'Mentors and co-authors, before submission rather than after rejection.'],
  ['Prefer the active voice', 'Shorter sentences, clearer attribution.'],
  ['Answer "so what"', 'The discussion must state the clinical relevance.'],
];

const MANAGERS = [
  ['Zotero', 'Free and open source, with strong browser integration', 'https://www.zotero.org/'],
  ['Mendeley', 'Free, with PDF annotation', 'https://www.mendeley.com/'],
  ['EndNote', 'Commercial; check for a BCM institutional licence', 'https://endnote.com/'],
];

const LinkRow = ({ title, subtitle, href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
    <div className="flex-1 min-w-0">
      <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{subtitle}</div>}
    </div>
    <ExternalLink size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
  </a>
);

const NumberedRows = ({ items }) => (
  <ol>
    {items.map(([title, text], i) => (
      <li key={i} className="flex items-start gap-3 px-4 py-3">
        <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${ACCENT}1f`, color: ACCENT }}>{i + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{text}</div>
        </div>
      </li>
    ))}
  </ol>
);

const JournalRow = ({ j }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] text-gray-900 dark:text-white">{j.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{j.publisher} · impact factor {j.impact} · accepts {j.accept}</div>
        </div>
        {open ? <ChevronUp size={18} className="text-gray-300 dark:text-gray-600" /> : <ChevronDown size={18} className="text-gray-300 dark:text-gray-600" />}
      </button>
      {open && (
        <div className="px-4 pb-3 -mt-1 text-sm text-gray-700 dark:text-gray-300 leading-snug space-y-2">
          <p>{j.note}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Decision in {j.time}.</p>
          <a href={j.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: ACCENT }}>Instructions for authors <ExternalLink size={14} /></a>
        </div>
      )}
    </div>
  );
};

const PublicationResourcesTab = () => (
  <div className="space-y-6">
    <Section title="Reporting guidelines">
      <Card>
        <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={BookOpen} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">Most journals require the checklist for the study type with the submission.</div></div>
        {GUIDELINES.map(([t, s, h]) => <LinkRow key={t} title={t} subtitle={s} href={h} />)}
      </Card>
    </Section>

    <Section title="Where each article type fits">
      <Card>
        {BY_TYPE.map(([t, s, j]) => (
          <div key={t} className="px-4 py-3">
            <div className="font-medium text-[15px] text-gray-900 dark:text-white">{t}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{s}</div>
            <div className="text-xs mt-1 font-medium" style={{ color: ACCENT }}>{j}</div>
          </div>
        ))}
      </Card>
    </Section>

    <Section title="Anesthesiology journals">
      <Card>{JOURNALS.map((j) => <JournalRow key={j.name} j={j} />)}</Card>
    </Section>

    <Section title="Predatory journals">
      <Card>
        <div className="px-4 py-3 flex items-start gap-3">
          <IconTile icon={ShieldAlert} accent="#b91c1c" />
          <div className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 leading-snug">
            <p>Predatory journals charge fees without real peer review or editorial work. Publishing in one damages a record and wastes the study. Signs:</p>
            <ul className="mt-2 space-y-1 pl-4 list-disc marker:text-gray-400 text-xs text-gray-600 dark:text-gray-400">{RED_FLAGS.map((f) => <li key={f}>{f}</li>)}</ul>
          </div>
        </div>
        {CHECKS.map(([t, s, h]) => <LinkRow key={t} title={t} subtitle={s} href={h} />)}
      </Card>
    </Section>

    <Section title="Writing">
      <Card>
        <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={PenLine} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">Seven habits of manuscripts that get accepted.</div></div>
        <NumberedRows items={WRITING} />
      </Card>
    </Section>

    <Section title="Reference managers">
      <Card>
        <div className="px-4 py-3 flex items-center gap-3"><IconTile icon={Library} accent={ACCENT} /><div className="text-sm text-gray-600 dark:text-gray-400">Pick one before the first draft, not the last.</div></div>
        {MANAGERS.map(([t, s, h]) => <LinkRow key={t} title={t} subtitle={s} href={h} />)}
      </Card>
    </Section>
  </div>
);

export default PublicationResourcesTab;
